import React from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineMail, HiOutlinePaperAirplane } from 'react-icons/hi';
import { FaSlack, FaWhatsapp } from 'react-icons/fa';
import BadgeStatus from '../../../atoms/BadgeStatus';
import MenuButton from '../../../atoms/MenuButton';
import { MessageComposition, Template } from '../../../../types/communication';
import { TEMPLATE_KINDS, TemplateKind, templateKind } from './templateKinds';

export type TemplateRowAction = 'open' | 'edit' | 'duplicate' | 'toggle';

const KIND_ICON: Record<TemplateKind, React.ComponentType<{ size?: number; className?: string }>> = {
    EMAIL_BODY: HiOutlineMail,
    EMAIL_ENVELOPE: HiOutlinePaperAirplane,
    INTERNAL_MESSAGE: FaSlack,
    EXTERNAL_MESSAGE: FaWhatsapp,
};

const KIND_TILE: Record<TemplateKind, string> = {
    EMAIL_BODY: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    EMAIL_ENVELOPE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    INTERNAL_MESSAGE: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300',
    EXTERNAL_MESSAGE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
};

interface TemplateRowProps {
    template: Template;
    composition: MessageComposition | undefined;
    striped: boolean;
    onOpen: () => void;
    onAction: (action: TemplateRowAction) => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({ template, composition, striped, onOpen, onAction }) => {
    const kindKey = templateKind(template);
    const kind = TEMPLATE_KINDS[kindKey];
    const KindIcon = KIND_ICON[kindKey];
    const active = template.status === 'ACTIVE';
    return (
        <tr
            onClick={onOpen}
            className={`${striped ? 'bg-slate-50 dark:bg-slate-800' : 'bg-white dark:bg-slate-900'} border-b border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-teal-50/60 dark:hover:bg-slate-700`}
        >
            <td className="py-2 px-2 max-w-0 w-full">
                <div className="flex items-center gap-3 min-w-0">
                    <span
                        className={`inline-flex items-center justify-center rounded-lg shrink-0 w-9 h-9 ${KIND_TILE[kindKey]}`}
                        title={kind.label}
                    >
                        <KindIcon size={18} className={kindKey === 'EMAIL_ENVELOPE' ? 'rotate-90' : undefined} />
                    </span>
                    <div className="min-w-0">
                        <div className="text-sm font-medium font-mono text-gray-900 dark:text-gray-100 truncate">{template.name}</div>
                        <div className="text-[11px] text-gray-400 truncate" title={template.description}>
                            {template.description || kind.description}
                        </div>
                    </div>
                </div>
            </td>
            <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                {composition ? (
                    <div className="min-w-0 max-w-[260px]">
                        <Link
                            to={`/communication/message-composition/${composition.id}`}
                            className="block truncate text-xs text-teal-700 dark:text-teal-300 hover:underline"
                            title={composition.description || composition.name}
                        >
                            {composition.description || composition.name}
                        </Link>
                        <div className="text-[11px] text-gray-400 truncate">
                            {[composition.groupName, composition.description ? composition.name : null]
                                .filter(Boolean)
                                .join(' · ') || composition.name}
                        </div>
                    </div>
                ) : (
                    <span className="inline-flex rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                        Livre
                    </span>
                )}
            </td>
            <td className="py-2 px-2">
                <BadgeStatus color={active ? 'green' : 'red'} text={active ? 'Ativo' : 'Inativo'} />
            </td>
            <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                <MenuButton
                    ariaLabel="Ações do modelo"
                    options={[
                        { key: 'open', label: 'Abrir', type: 'item' },
                        {
                            key: 'edit',
                            label: 'Editar',
                            type: 'item',
                        },
                        { key: 'duplicate', label: 'Duplicar', type: 'item' },
                        { key: 'sep', type: 'separator' },
                        {
                            key: 'toggle',
                            label: active ? 'Desativar…' : 'Ativar…',
                            type: 'item',
                            backgroundColor: active ? '#DC2626' : '#16A34A',
                        },
                    ]}
                    onSelect={(key) => onAction(key as TemplateRowAction)}
                />
            </td>
        </tr>
    );
};

export default TemplateRow;
